import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Trophy, Flame } from 'lucide-react';

const XP_PER_LEVEL = 250;

// Compact XP/Level/Streak indicator shown in the header for logged-in users.
// Polls /games/state on mount and listens for 'kc:xp-updated' window events
// so it refreshes immediately after any XP-earning action.
export const XpBar = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/games/state');
      setStats(r.data);
    } catch {
      /* not authed yet */
    }
  };

  useEffect(() => {
    if (!user) { setStats(null); return; }
    load();
    const onUpdate = () => load();
    window.addEventListener('kc:xp-updated', onUpdate);
    return () => window.removeEventListener('kc:xp-updated', onUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || !stats) return null;

  const xp = stats.xp || 0;
  const level = stats.level || 1;
  const streak = stats.streak || 0;
  const xpInLevel = xp % XP_PER_LEVEL;
  const pct = Math.min(100, (xpInLevel / XP_PER_LEVEL) * 100);

  return (
    <Link
      to="/games"
      className="hidden lg:flex items-center gap-3 px-3 h-10 rounded-full bg-ink text-white hover:bg-black/85 transition-colors"
      data-testid="header-xp-bar"
      title={`Level ${level} \u00B7 ${xp} XP \u00B7 ${streak}d streak`}
    >
      <div className="flex items-center gap-1.5">
        <Trophy size={14} className="text-[color:var(--kc-gold)]" />
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">Lvl</span>
        <span className="font-display text-lg leading-none text-[color:var(--kc-gold)] tabular-nums" data-testid="xp-level">{level}</span>
      </div>

      <div className="relative w-24 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full bg-[color:var(--kc-gold)] transition-all"
          style={{ width: `${pct}%` }}
          data-testid="xp-progress"
        />
      </div>

      <div className="flex items-center gap-1">
        <Flame size={13} className="text-[color:var(--kc-gold)]" />
        <span className="text-sm font-semibold tabular-nums" data-testid="xp-streak">{streak}</span>
      </div>
    </Link>
  );
};
